import type { BadgeVariant } from '../../components/ui/Badge';
import type { KhoaDich } from '../../i18n';
import type { LoiTep, MaLoiPhanHe, MaLoiTaiLieu } from './nghiep-vu/payload';
import type { AccentName, Category, ModuleId, SlugPhanHe } from './types';

/**
 * Mã quyền module mà từng nút cần — chép đúng 22 mã của `modules/bao-cao/module.yaml` (bảng API ở README
 * module). Không đủ quyền thì ẩn nút (ADR-UI-0005); server vẫn là chốt cuối.
 */
export const QUYEN = {
  tongQuanDoc: 'bao-cao:tong-quan:read',
  tongQuanSua: 'bao-cao:tong-quan:update',
  taiChinhDoc: 'bao-cao:tai-chinh:read',
  taiChinhSua: 'bao-cao:tai-chinh:update',
  duAnDoc: 'bao-cao:du-an:read',
  duAnSua: 'bao-cao:du-an:update',
  kinhDoanhDoc: 'bao-cao:kinh-doanh:read',
  kinhDoanhSua: 'bao-cao:kinh-doanh:update',
  nhanSuDoc: 'bao-cao:nhan-su:read',
  nhanSuSua: 'bao-cao:nhan-su:update',
  taiLieuDoc: 'bao-cao:tai-lieu:read',
  taiLieuTao: 'bao-cao:tai-lieu:create',
  taiLieuSua: 'bao-cao:tai-lieu:update',
  taiLieuXoa: 'bao-cao:tai-lieu:delete',
  taiLieuNhap: 'bao-cao:tai-lieu:import',
  thuMucDoc: 'bao-cao:thu-muc:read',
  thuMucTao: 'bao-cao:thu-muc:create',
  thuMucSua: 'bao-cao:thu-muc:update',
  thuMucXoa: 'bao-cao:thu-muc:delete',
  nguonDoc: 'bao-cao:nguon:read',
  nguonTao: 'bao-cao:nguon:create',
  nguonSua: 'bao-cao:nguon:update',
} as const;

/** Slug trên URL ↔ `moduleId` của API. Enum đóng — không đổi tuỳ tiện. */
export const SLUG_TO_MODULE: Record<SlugPhanHe, ModuleId> = {
  'tong-quan': 'overview',
  'tai-chinh': 'finance',
  'du-an': 'projects',
  'kinh-doanh': 'sales',
  'nhan-su': 'people',
};

/**
 * Phân hệ ↔ quyền: mỗi phân hệ một cặp `read`/`update` (`bao-cao:{slug}:…`), như backend tính mã quyền
 * từ `{moduleId}` (README module, mục API). Người xem nhân sự không thấy tài chính.
 */
export const QUYEN_PHAN_HE: Record<SlugPhanHe, { doc: keyof typeof QUYEN; sua: keyof typeof QUYEN }> = {
  'tong-quan': { doc: 'tongQuanDoc', sua: 'tongQuanSua' },
  'tai-chinh': { doc: 'taiChinhDoc', sua: 'taiChinhSua' },
  'du-an': { doc: 'duAnDoc', sua: 'duAnSua' },
  'kinh-doanh': { doc: 'kinhDoanhDoc', sua: 'kinhDoanhSua' },
  'nhan-su': { doc: 'nhanSuDoc', sua: 'nhanSuSua' },
};

/** Phân hệ RBAC của cổng tenant cho khối "Tích hợp hệ thống ngoài": màn `/he-thong/khoa-api` (ADR-0068). */
export const PHAN_HE_KHOA_API = 'API_KEY';

/** Số tài liệu mỗi lần tải (`limit`, backend nhận 1–100). */
export const MOT_TRANG = 50;

/**
 * Ô màu nhấn: `accentName` là **tên token màu** gửi xuống app, nên ô chọn vẽ đúng màu sắp gửi đi. Bậc
 * `-500` là bậc cho chấm/thanh (≥ 3:1), không phải bậc chữ. Viết đủ tám class (Tailwind quét tĩnh).
 */
export const O_MAU: Record<AccentName, string> = {
  primary: 'bg-primary-500',
  success: 'bg-success-500',
  warning: 'bg-warning-500',
  danger: 'bg-danger-500',
  violet: 'bg-violet-500',
  teal: 'bg-teal-500',
  sky: 'bg-sky-500',
  orange: 'bg-orange-500',
};

type BienThePhanLoai = Extract<BadgeVariant, 'teal' | 'violet' | 'orange' | 'info' | 'neutral'>;

/**
 * Danh mục tài liệu — nhóm biến thể **phân loại** của `Badge`: danh mục là *loại*, không phải mức độ.
 * Tài chính không được tô `success` (đọc thành "đang tốt"). `other` xám vì không thuộc phân hệ nào.
 */
export const DANH_MUC: Record<Category, { khoa: KhoaDich; variant: BienThePhanLoai }> = {
  finance: { khoa: 'baoCaoDoanhNghiep.danhMuc.finance', variant: 'teal' },
  projects: { khoa: 'baoCaoDoanhNghiep.danhMuc.projects', variant: 'violet' },
  sales: { khoa: 'baoCaoDoanhNghiep.danhMuc.sales', variant: 'orange' },
  people: { khoa: 'baoCaoDoanhNghiep.danhMuc.people', variant: 'info' },
  other: { khoa: 'baoCaoDoanhNghiep.danhMuc.other', variant: 'neutral' },
};

type BienTheTrangThai = Extract<BadgeVariant, 'success' | 'neutral' | 'primary'>;

/** Trạng thái nguồn — nhóm **trạng thái**: đang nhận ghi xanh, đã ngừng xám (chủ động, không phải sự cố). */
export const TRANG_THAI_NGUON: Record<'hoatDong' | 'dungHoatDong', { khoa: KhoaDich; variant: BienTheTrangThai }> = {
  hoatDong: { khoa: 'baoCaoDoanhNghiep.nguon.hoatDong', variant: 'success' },
  dungHoatDong: { khoa: 'baoCaoDoanhNghiep.nguon.dungHoatDong', variant: 'neutral' },
};

/** Nguồn mặc định: xanh nhấn — đánh dấu, không mang nghĩa tốt/xấu. */
export const BADGE_MAC_DINH: { khoa: KhoaDich; variant: BienTheTrangThai } = {
  khoa: 'baoCaoDoanhNghiep.nguon.macDinh',
  variant: 'primary',
};

/** Màu chữ của mũi tên xu hướng — lấy từ `trend`, không từ dấu của `delta`. */
export const MAU_XU_HUONG = {
  up: 'text-success-700',
  down: 'text-danger-700',
  flat: 'text-content-secondary',
} as const;

export const KHOA_LOI_PHAN_HE: Record<MaLoiPhanHe, KhoaDich> = {
  trong: 'baoCaoDoanhNghiep.loi.batBuoc',
  trung: 'baoCaoDoanhNghiep.loi.maTrung',
  sparkSai: 'baoCaoDoanhNghiep.loi.sparkSai',
  sparkThieu: 'baoCaoDoanhNghiep.loi.sparkThieu',
};

export const KHOA_LOI_TAI_LIEU: Record<MaLoiTaiLieu, KhoaDich> = {
  trong: 'baoCaoDoanhNghiep.loi.batBuoc',
  urlSai: 'baoCaoDoanhNghiep.loi.urlSai',
  chuaChonTep: 'baoCaoDoanhNghiep.loi.chuaChonTep',
  khongNhan: 'baoCaoDoanhNghiep.loi.tepKhongNhan',
  quaLon: 'baoCaoDoanhNghiep.loi.tepQuaLon',
  rong: 'baoCaoDoanhNghiep.loi.tepRong',
} satisfies Record<MaLoiTaiLieu | LoiTep, KhoaDich>;

/**
 * Mã lỗi riêng của module → câu nói cách xử lý. `detail` của backend vẫn được ưu tiên khi mã không có
 * ở đây (câu backend nói đúng tên trùng, thư mục nào).
 */
export const KHOA_LOI_MA: Partial<Record<string, KhoaDich>> = {
  BAO_CAO_SOURCE_INACTIVE: 'baoCaoDoanhNghiep.loi.nguonDaNgung',
  BAO_CAO_SOURCE_DEFAULT_LOCKED: 'baoCaoDoanhNghiep.loi.nguonMacDinhKhoa',
  BAO_CAO_FOLDER_NOT_EMPTY: 'baoCaoDoanhNghiep.loi.thuMucConNoiDung',
  BAO_CAO_FOLDER_CYCLE: 'baoCaoDoanhNghiep.loi.thuMucChuTrinh',
  BAO_CAO_FILE_TYPE_NOT_SUPPORTED: 'baoCaoDoanhNghiep.loi.tepKhongNhan',
  BAO_CAO_FILE_TOO_LARGE: 'baoCaoDoanhNghiep.loi.tepQuaLon',
};
